/**
 * Neon Auth Client Integration
 * Powered by Better Auth
 *
 * This module provides authentication functionality using Neon Auth
 * with the Better Auth framework under the hood.
 */

import { createAuthClient } from '@neondatabase/neon-js/auth';
import { BetterAuthReactAdapter } from '@neondatabase/neon-js/auth/react/adapters';

// Auth URL from Neon Console
const NEON_AUTH_URL =
  import.meta.env.VITE_NEON_AUTH_URL ||
  'https://ep-little-union-agjv4q15.neonauth.c-2.eu-central-1.aws.neon.tech/neondb/auth';

// Create the auth client directly (we don't use the Data API)
export const authClient = createAuthClient(NEON_AUTH_URL, {
  adapter: BetterAuthReactAdapter(),
});

// Type definitions for user and session
export interface NeonUser {
  id: string;
  email: string;
  name?: string;
  image?: string;
  emailVerified?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface NeonSession {
  id: string;
  userId: string;
  token: string;
  expiresAt: Date;
  createdAt: Date;
  updatedAt: Date;
  ipAddress?: string;
  userAgent?: string;
}

export interface AuthState {
  user: NeonUser | null;
  session: NeonSession | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

// Auth methods wrapper for easier usage
export const neonAuth = {
  /**
   * Sign up with email and password
   */
  signUp: async (params: {
    email: string;
    password: string;
    name?: string;
    callbackURL?: string;
  }) => {
    return authClient.signUp.email({
      email: params.email,
      password: params.password,
      name: params.name || '',
      callbackURL: params.callbackURL || '/',
    });
  },

  /**
   * Sign in with email and password
   */
  signIn: async (params: {
    email: string;
    password: string;
    rememberMe?: boolean;
    callbackURL?: string;
  }) => {
    return authClient.signIn.email({
      email: params.email,
      password: params.password,
      rememberMe: params.rememberMe ?? true,
      callbackURL: params.callbackURL || '/',
    });
  },

  /**
   * Sign in with OAuth provider (Google, GitHub, etc.)
   */
  signInWithOAuth: async (params: {
    provider: 'google' | 'github' | 'apple' | 'microsoft';
    callbackURL?: string;
    errorCallbackURL?: string;
  }) => {
    return authClient.signIn.social({
      provider: params.provider,
      callbackURL: params.callbackURL || '/',
      errorCallbackURL: params.errorCallbackURL || '/auth?error=oauth_error',
    });
  },

  /**
   * Sign out the current user
   */
  signOut: async () => {
    return authClient.signOut();
  },

  /**
   * Get the current session
   */
  getSession: async () => {
    return authClient.getSession();
  },

  /**
   * Request password reset
   */
  forgotPassword: async (email: string, redirectTo?: string) => {
    return authClient.forgetPassword({
      email,
      redirectTo: redirectTo || `${window.location.origin}/auth/reset-password`,
    });
  },

  /**
   * Reset password with token
   */
  resetPassword: async (params: { newPassword: string; token?: string }) => {
    return authClient.resetPassword({
      newPassword: params.newPassword,
      token: params.token,
    });
  },

  /**
   * Update user profile
   */
  updateUser: async (params: { name?: string; image?: string }) => {
    return authClient.updateUser(params);
  },

  /**
   * Change password (for authenticated users)
   */
  changePassword: async (params: { currentPassword: string; newPassword: string }) => {
    return authClient.changePassword({
      currentPassword: params.currentPassword,
      newPassword: params.newPassword,
    });
  },

  /**
   * Verify email with token
   */
  verifyEmail: async (token: string) => {
    return authClient.verifyEmail({ token });
  },

  /**
   * List active sessions
   */
  listSessions: async () => {
    return authClient.listSessions();
  },

  /**
   * Revoke a specific session
   */
  revokeSession: async (sessionId: string) => {
    return authClient.revokeSession({ id: sessionId });
  },
};

export default neonAuth;
